import React from "react";
import { Helmet } from "react-helmet-async";
import { motion, Variants } from "framer-motion";
import {
  Utensils,
  Wheat,
  HeartHandshake,
  AlertCircle,
  Activity,
  HeartCrack,
  Globe,
  ShieldCheck,
} from "lucide-react";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/hunger-support`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;

const pageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  name: "Hunger Support | SEVAA Foundation",
  alternateName: "Zero Hunger Mission - Food Security and Meal Support",
  url: pageUrl,
  inLanguage: "en-IN",
  description:
    "SEVAA Foundation supports hunger relief and food security through daily meals, community kitchens, ration kits, disaster relief food support and nutrition support for poor, tribal and rural communities.",
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: logoImage,
    },
    image: defaultImage,
    slogan: "सेवा परमो धर्मः",
    areaServed: [
      { "@type": "Country", name: "India" },
      { "@type": "Place", name: "Jharkhand" },
      { "@type": "Place", name: "Chatra" },
      { "@type": "Place", name: "Pakur" },
    ],
  },
  about: [
    "Hunger Support",
    "Food Security",
    "Food Donation",
    "Ration Kit Donation",
    "Daily Meals",
    "Community Kitchen",
    "Nutrition Support",
    "Child Nutrition",
    "Tribal Food Support",
    "Disaster Relief Food",
    "Zero Hunger Mission"
  ],
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultImage,
    width: 1200,
    height: 630,
  },
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${pageUrl}#service`,
  name: "Hunger Support and Food Security Program",
  serviceType: "Food Donation and Hunger Relief",
  url: pageUrl,
  provider: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
  },
  areaServed: {
    "@type": "Country",
    name: "India",
  },
  description:
    "SEVAA Foundation provides food security support through community kitchens, hot meals, ration kits and emergency food relief for poor, tribal, rural and vulnerable communities.",
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Hunger Support",
      item: pageUrl,
    },
  ],
};

const HungerSupport: React.FC = () => {
  const staggerContainerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const gridItemVariants: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <>
      <Helmet>
        <title>Hunger Support NGO | SEVAA Foundation</title>

        <meta
          name="title"
          content="Hunger Support NGO | SEVAA Foundation"
        />

        <meta
          name="description"
          content="Donate to SEVAA Foundation's hunger relief mission for daily meals, ration kits, community kitchens, child nutrition and food security for poor families."
        />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Hunger Support NGO | Food Donation & Ration Kits" />
        <meta property="og:description" content="Support food security, daily meals, ration kits, child nutrition and hunger relief for poor, tribal and rural families." />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="SEVAA Foundation hunger support and food donation program" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Hunger Support NGO | Donate Food & Ration Kits" />
        <meta name="twitter:description" content="Support SEVAA Foundation's hunger relief mission for food security, daily meals, ration kits and nutrition support." />
        <meta name="twitter:image" content={defaultImage} />
        <meta name="twitter:image:alt" content="SEVAA Foundation hunger support and food donation program" />

        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-slate-50 min-h-screen font-poppins pb-20">
        {/* 1. HERO SECTION */}
        <section
          aria-labelledby="hunger-support-heading"
          className="relative w-full h-[400px] md:h-[500px]"
        >
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=2070&auto=format&fit=crop"
              alt="SEVAA Foundation hunger support and food security mission for poor tribal and rural communities"
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />

            <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 to-black/60"></div>
          </div>

          <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <span className="inline-block py-1 px-3 rounded-full bg-orange-500/20 text-orange-400 font-bold text-xs uppercase tracking-widest mb-4 border border-orange-500/30">
                Zero Hunger Mission
              </span>

              <h1
                id="hunger-support-heading"
                className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6"
              >
                Food Security in India <br />
                <span className="text-orange-400">
                  A Widespread Concern
                </span>
              </h1>

              <p className="text-teal-50 text-base md:text-lg font-medium leading-relaxed mb-8 max-w-2xl opacity-90">
                Access to enough nutritious food for a healthy life is still a
                major issue, especially among children in poor, tribal and rural
                communities.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 2. THE HARSH REALITY */}
        <section
          aria-labelledby="hunger-reality-heading"
          className="max-w-7xl mx-auto px-6 lg:px-8 -mt-20 relative z-10 mb-20"
        >
          <h2 id="hunger-reality-heading" className="sr-only">
            Hunger and Nutrition Reality in India
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -10 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 border-b-[6px] border-b-rose-500 transition-all"
            >
              <AlertCircle className="w-12 h-12 text-rose-500 mb-6" />

              <h3 className="text-4xl font-black text-slate-900 mb-2">
                40<span className="text-xl">%</span>
              </h3>

              <p className="text-slate-600 font-medium text-sm leading-relaxed">
                Of children under five in India live in severe child food
                poverty, making nutrition support and food security urgent for
                vulnerable families.
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -10 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 border-b-[6px] border-b-amber-500 transition-all"
            >
              <Activity className="w-12 h-12 text-amber-500 mb-6" />

              <h3 className="text-4xl font-black text-slate-900 mb-2">
                12<span className="text-xl">%</span>
              </h3>

              <p className="text-slate-600 font-medium text-sm leading-relaxed">
                Of the population was undernourished in 2024, showing the need
                for affordable healthy diets, ration support and community food
                programs.
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -10 }}
              viewport={{ once: true }}
              className="bg-teal-800 p-8 rounded-3xl shadow-xl border border-teal-700 text-white border-b-[6px] border-b-teal-400 transition-all"
            >
              <HeartCrack className="w-12 h-12 text-teal-300 mb-6" />

              <h3 className="text-4xl font-black mb-2">
                200<span className="text-xl">M+</span>
              </h3>

              <p className="text-teal-100 font-medium text-sm leading-relaxed">
                Women aged 15-49 are anaemic, affecting family growth, maternal
                health, child nutrition and long-term community well-being.
              </p>
            </motion.article>
          </div>
        </section>

        {/* 3. THE GROUND REALITY */}
        <section
          aria-labelledby="nutrition-crisis-heading"
          className="max-w-7xl mx-auto px-6 lg:px-8 py-16"
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3">
                <span className="w-12 h-[2px] bg-teal-600"></span>
                <span className="text-teal-700 font-bold text-xs uppercase tracking-widest">
                  The Nutrition Crisis
                </span>
              </div>

              <h2
                id="nutrition-crisis-heading"
                className="text-3xl md:text-4xl font-black text-slate-900 leading-[1.2]"
              >
                Why nutritious food is more than just a meal
              </h2>

              <p className="text-slate-600 leading-relaxed font-medium">
                Severe food poverty means children are often limited to only one
                or two food groups daily. This lack of a diverse diet directly
                contributes to poor physical and cognitive development.
              </p>

              <p className="text-slate-600 leading-relaxed font-medium">
                India ranks among countries with high levels of severe child
                food poverty. Access to nutritious food is essential for
                survival, learning, health and overall well-being.
              </p>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-2xl mt-8">
                <p className="text-slate-800 font-bold italic">
                  “Food security is not just about filling plates. It is about
                  protecting dignity, health, learning and hope for vulnerable
                  families.”
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl"
            >
              <img
                src="https://www.feesers.com/wp-content/uploads/2023/09/01-A-Guide-to-the-Types-of-Food-Service-distribution.jpg"
                alt="Food distribution and nutrition support by SEVAA Foundation hunger relief NGO"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </section>

        {/* 4. SEVAA'S INTERVENTION */}
        <section
          aria-labelledby="hunger-strategy-heading"
          className="bg-white py-24 border-t border-slate-200 mt-10"
        >
          <div className="max-w-[90rem] mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <h2
                id="hunger-strategy-heading"
                className="text-3xl md:text-4xl font-black text-slate-900 mb-4"
              >
                Our Fighting Hunger Strategy
              </h2>

              <p className="text-slate-500 font-medium text-lg">
                We serve hot nutritious meals, dry ration kits, emergency food
                relief and essential nutrition support to people in need.
              </p>
            </div>

            <motion.div
              variants={staggerContainerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-fr"
            >
              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src="https://images.unsplash.com/photo-1594708767771-a7502209ff51?q=80&w=2070"
                  alt="Community kitchen and daily meal support by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-orange-500/10 hover:border-orange-200 transition-all group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-orange-50 transition-colors">
                  <Utensils className="w-7 h-7 text-orange-500" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Daily Kitchens
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Running community kitchens to provide fresh, hot and
                  nutritious daily meals to vulnerable families, children and
                  people struggling with hunger.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src="https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?q=80&w=2070"
                  alt="Ration kit donation and dry food support by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-teal-500/10 hover:border-teal-200 transition-all group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-teal-50 transition-colors">
                  <Wheat className="w-7 h-7 text-teal-600" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Ration Kits
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Distributing monthly dry ration kits to ensure poor and rural
                  families have basic food supplies for dignity, nutrition and
                  survival.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src="https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?q=80&w=2070"
                  alt="Emergency food and disaster relief support by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-amber-500/10 hover:border-amber-200 transition-all group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-amber-50 transition-colors">
                  <HeartHandshake className="w-7 h-7 text-amber-500" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Disaster Relief
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Providing emergency food, clean water and basic relief during
                  natural crises, disasters and emergencies.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src="https://images.unsplash.com/photo-1593113630400-ea4288922497?q=80&w=2070"
                  alt="Food donation impact and community support by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-orange-600 p-8 rounded-[2rem] shadow-lg flex flex-col justify-center hover:-translate-y-2 hover:shadow-orange-600/40 transition-all min-h-[350px] text-white"
              >
                <div className="w-14 h-14 bg-orange-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <ShieldCheck className="w-7 h-7 text-white" />
                </div>

                <h3 className="text-xl font-bold mb-3">Our Impact</h3>

                <p className="text-orange-50 font-medium text-sm leading-relaxed mb-6">
                  Fighting hunger supports education and health outcomes across
                  generations. We work to ensure no one goes to sleep hungry.
                </p>

                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-white text-orange-600 font-bold px-6 py-2.5 rounded-full transition-colors text-sm w-fit"
                  aria-label="Support SEVAA Foundation hunger relief mission"
                >
                  Support Now
                  <Globe className="w-4 h-4" />
                </a>
              </motion.article>
            </motion.div>
          </div>
        </section>

      </main>
    </>
  );
};

export default HungerSupport;